import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { concatMap, EMPTY, finalize, forkJoin, from, fromEvent, merge, of, Subscription, switchMap, toArray } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { BoardService } from '../../../services/board.service';
import { NotificationService } from '../../../services/notifications.service';
import { FUNNEL_TYPE } from '../../../enum/constants';
import { AddressInput, getCardPhoneValue, IAddress, ICardEmail, ICardPhone, ICardV2 } from '../../../interfaces/card';
import { ICardSection, ICardSectionColumn } from '../../../interfaces/card-section.interface';
import { environment } from '../../../../environments/environment';
import { BaseService } from '../../../services/base.service';
import { BoardV3RealtimeService } from '../board-v3-realtime.service';
import {
  BoardColumn,
  BoardCompany,
  BoardContact,
  BoardField,
  CardViewer,
} from '../board-v3.types';
import { BoardV3PresenceService } from '../board-v3-presence.service';
import { BoardV3CardGeneralPanelComponent } from './card-general-panel/card-general-panel.component';
import { AuthService } from '../../../services/auth.service';
import { StackedSidebarService } from '../../../services/stacked-sidebar.service';
import { SidebarType } from '../../layout/stacked-sidebar/stacked-sidebar.interface';
import {
  customFieldPhoneValidator,
  customFieldPriceValidator,
  getCustomFieldMaxLength,
  getCustomFieldValidators,
  normalizeCustomFieldValue,
  normalizePhonePayloadValue,
  sanitizePhoneInputValue,
} from '../custom-field.validators';
import {
  CurrencyOption,
  formatMoneyAmount,
  formatPriceFieldValue,
  getCurrencyOptions,
  isPriceFieldValue,
} from '../price-field.utils';
import { formatFullName } from '../../../helpers';
import { SECTION_CLIENTS_KEY } from '../sidebars/section-fields-dialog/section-fields-dialog.component';
import { TimelineCompanyChange, TimelineContactChange } from './card-timeline/timeline-update/timeline-update.component';
import { TIMELINE_FILTERS, TimelineComponent } from '../../timeline/timeline.component';
import { TIMELINE_ENTITY_TYPE } from '../../../interfaces/timeline.interface';
import { formatAddress, getAddressMapUrl, isSameAddressList, toAddressInput } from '../address.utils';
import { WarehouseService } from '../../../services/warehouse.service';
import { BoardPaymentService } from '../../../services/board-payment.service';
import { CardItemsBlock } from '../../warehouse/warehouse.types';
import { CardPaymentsBlock } from '../../payments/payments.types';
import { CardDetailTab } from '../../tabs/enums/tabs.enum';
import { APPLICATION_TYPE } from '../../../modules/market/components/market/market.interface';
import { Tab } from '../../tabs-v2/interfaces/tab.interface';
import { getApiError, getVariantTitle } from '../../warehouse/warehouse.utils';

interface CardDetailForm {
  name: FormControl<string | null>;
  price: FormControl<number | string | null>;
  currency: FormControl<string | null>;
  description: FormControl<string | null>;
  assigned_id: FormControl<string | null>;
  phones: FormControl<string | null>;
  emails: FormControl<string | null>;
  section_fields: FormGroup<Record<string, FormControl<unknown>>>;
}

interface VisibleCardSection extends ICardSection {
  visibleColumns: ICardSectionColumn[];
}

type EditableCardField = 'name' | 'price' | 'assigned_id' | 'description' | 'phones' | 'emails';

/** UTM tags are card columns, not custom fields: read from and sent as top-level card properties */
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
type UtmKey = (typeof UTM_KEYS)[number];
const isUtmKey = (key: string): key is UtmKey => (UTM_KEYS as readonly string[]).includes(key);

interface CardManager {
  id: string | number;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  avatar_url?: string | null;
}

@Component({
  selector: 'app-board-v3-card-detail',
  templateUrl: './card-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoardV3CardDetailComponent implements OnInit, OnChanges, OnDestroy {
  @Input() cardId!: string;
  @Input() entityType!: FUNNEL_TYPE;
  @Output() close = new EventEmitter<unknown>();
  @ViewChildren('editableControl') private editableControls!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('generalPanel') private generalPanel?: BoardV3CardGeneralPanelComponent;
  @ViewChild(TimelineComponent) private timeline?: TimelineComponent;

  public readonly boardService = inject(BoardService);
  private readonly realtimeService = inject(BoardV3RealtimeService);
  private readonly baseService = inject(BaseService);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly stackedSidebarService = inject(StackedSidebarService);
  private readonly presenceService = inject(BoardV3PresenceService);
  private readonly warehouseService = inject(WarehouseService);
  private readonly paymentService = inject(BoardPaymentService);

  get isCardLoaded(): boolean {
    return this.boardService.isCardLoaded;
  }

  loadError: boolean = false;

  sections: ICardSection[] = [];
  visibleSections: VisibleCardSection[] = [];
  readonly clientsKey = SECTION_CLIENTS_KEY;
  managers: CardManager[] = [];
  viewers: CardViewer[] = [];
  sectionDrafts: ICardSection[] = [];
  columns: BoardColumn[] = [];
  movingStage = false;
  readonly CardDetailTab = CardDetailTab;
  activeDetailTab: CardDetailTab = CardDetailTab.GENERAL;
  /** Rebuilt only when its inputs change: the tabs input must not get a new array on every check */
  private detailTabsCache: { key: string; tabs: Tab<CardDetailTab>[] } | null = null;
  readonly TIMELINE_ENTITY_TYPE = TIMELINE_ENTITY_TYPE;
  readonly TIMELINE_FILTERS = TIMELINE_FILTERS;
  fieldDefinitions = new Map<string, BoardField>();
  form = new FormGroup<CardDetailForm>({
    name: new FormControl(''),
    price: new FormControl(null, [customFieldPriceValidator]),
    currency: new FormControl('', [Validators.required]),
    description: new FormControl(''),
    assigned_id: new FormControl(''),
    phones: new FormControl(''),
    emails: new FormControl(''),
    section_fields: new FormGroup<Record<string, FormControl<unknown>>>({}),
  });

  readonly currencyOptions: CurrencyOption[] = getCurrencyOptions();
  loading = false;
  saving = false;
  showCloseConfirmation = false;
  conflict: { authorName: string; changes: string[] } | null = null;
  activeField: string | null = null;
  canEditSections = false;
  sectionEditMode = false;
  sectionDraftDirty = false;
  savingSections = false;
  phonesArray: FormArray<FormControl<string>> = new FormArray<FormControl<string>>([]);
  emailsArray: FormArray<FormControl<string>> = new FormArray<FormControl<string>>([]);
  readonly addressesControl = new FormControl<AddressInput[]>([], { nonNullable: true });
  protected readonly getAddressMapUrl = getAddressMapUrl;
  private detailUpdateSubscription?: Subscription;
  private presenceSubscription?: Subscription;
  private presenceHeartbeatSubscription?: Subscription;
  private presenceCardId?: string;
  private presenceCompanyId?: string;
  private presenceFunnelId?: string;
  private keepEditFocusUntil = 0;

  private readonly readonlySectionKeys = new Set(['phase', 'created_at', 'updated_at', 'updated_by']);

  private readonly systemSectionKeys = new Set([
    'ai',
    'assigned_id',
    'big_comment',
    'binotel_id',
    'created_at',
    'currency',
    'description',
    'email',
    'emails',
    'link',
    'name',
    'phase',
    'phone',
    'phones',
    'price',
    'addresses',
    'updated_at',
    'updated_by',
  ]);

  ngOnInit(): void {
    this.subscribeToConnectionRestored();
    this.subscribeToCardRefresh();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cardId'] && this.cardId) {
      if (this.boardService.card?.id !== this.cardId) {
        this.boardService.resetCard();
        // The top tabs are created again with the loaded card and start on the general tab
        this.activeDetailTab = CardDetailTab.GENERAL;
      }
      this.unsubscribeFromCardPresence();
      this.subscribeToCardDetailUpdates(this.cardId);
      this.viewers = [];
      this.showCloseConfirmation = false;
      this.conflict = null;
      this.sectionEditMode = false;
      this.sectionDraftDirty = false;
      this.sectionDrafts = [];
      this.loadCard();
    }
  }

  ngOnDestroy(): void {
    this.detailUpdateSubscription?.unsubscribe();
    this.detailUpdateSubscription = undefined;
    this.unsubscribeFromCardPresence();
    this.boardService.resetCard();
  }

  private subscribeToConnectionRestored(): void {
    this.realtimeService
      .connectionRestored$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.cardId || this.saving || this.savingSections || this.hasChanges) return;

        this.loadCard();
      });
  }

  private subscribeToCardRefresh(): void {
    merge(
      fromEvent(window, 'focus'),
      fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible')),
      this.boardService.cardRefreshRequested$.pipe(filter((cardId) => cardId === this.cardId)),
    ).pipe(
      filter(() => this.boardService.card?.id === this.cardId && !this.saving && !this.savingSections),
      switchMap(() => this.boardService.getCardDetails(this.cardId).pipe(catchError(() => EMPTY))),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((card) => this.applyCardDetailUpdate(card));
  }

  /** Product items and payments belong to deals: a lead card has neither the products nor the payments tab */
  get isDealCard(): boolean {
    return this.entityType !== FUNNEL_TYPE.LEAD;
  }

  /** The Nova Poshta tab comes with the installed application */
  get hasNovaPoshta(): boolean {
    const keys: string[] = this.baseService.currentCompanyInfo?.application_keys ?? [];
    return this.isDealCard && keys.includes(APPLICATION_TYPE.NOVA_POSHTA);
  }

  get detailTabs(): Tab<CardDetailTab>[] {
    const productsCount = this.boardService.card?.items?.length ?? 0;
    const key = `${this.isDealCard}:${productsCount}:${this.hasNovaPoshta}`;
    if (this.detailTabsCache?.key !== key) {
      const tabs: Tab<CardDetailTab>[] = [
        { name: 'board_v3_card_tab_general', value: CardDetailTab.GENERAL, default: true },
      ];
      if (this.isDealCard) {
        tabs.push(
          { name: 'board_v3_card_tab_products', value: CardDetailTab.PRODUCTS, count: productsCount },
          { name: 'board_v3_card_tab_payments', value: CardDetailTab.PAYMENTS },
        );
      }
      if (this.hasNovaPoshta) tabs.push({ name: 'board_v3_card_tab_nova_poshta', value: CardDetailTab.NOVA_POSHTA });
      this.detailTabsCache = { key, tabs };
    }
    return this.detailTabsCache.tabs;
  }

  onSelectDetailTab(tab: Tab<CardDetailTab>): void {
    this.activeDetailTab = tab.value;
  }

  get hasChanges(): boolean {
    if (this.sectionEditMode) return this.sectionDraftDirty || this.form.dirty;

    return this.form.dirty || this.phonesArray.dirty || this.emailsArray.dirty || this.addressesControl.dirty;
  }

  get cardPhones(): ICardPhone[] {
    return this.boardService.card.phones ?? [];
  }

  get cardEmails(): ICardEmail[] {
    return this.boardService.card.emails ?? [];
  }

  get cardAddresses(): IAddress[] {
    return this.boardService.card.addresses ?? [];
  }

  isPhoneColumn(column: ICardSectionColumn): boolean {
    return column.key === 'phone' || column.key === 'phones';
  }

  isEmailColumn(column: ICardSectionColumn): boolean {
    return column.key === 'email' || column.key === 'emails';
  }

  isAddressColumn(column: ICardSectionColumn): boolean {
    return column.key === 'addresses';
  }

  formatCardAddress(address: AddressInput): string {
    return formatAddress(address, this.getTranslation('address_apartment_short'));
  }

  addPhoneRow(): void {
    this.phonesArray.push(new FormControl('', { nonNullable: true, validators: [customFieldPhoneValidator] }));
    this.phonesArray.markAsDirty();
  }

  removePhoneRow(index: number): void {
    if (this.phonesArray.length <= 1) return;

    this.phonesArray.removeAt(index);
    this.phonesArray.markAsDirty();
  }

  addEmailRow(): void {
    this.emailsArray.push(new FormControl('', { nonNullable: true }));
    this.emailsArray.markAsDirty();
  }

  removeEmailRow(index: number): void {
    if (this.emailsArray.length <= 1) return;

    this.emailsArray.removeAt(index);
    this.emailsArray.markAsDirty();
  }

  getFieldArray(column: ICardSectionColumn): FormArray<FormControl<string>> {
    return this.isPhoneColumn(column) ? this.phonesArray : this.emailsArray;
  }

  getPhoneEmailDisplayValues(column: ICardSectionColumn): string[] {
    return this.getFieldArray(column)
      .controls.map((control) => {
        const value = control.value.trim();
        return this.isPhoneColumn(column) && value ? this.formatPhoneValue(value) : value;
      })
      .filter(Boolean);
  }

  getPhoneEmailHref(column: ICardSectionColumn, value: string): string {
    return this.isPhoneColumn(column) ? `tel:${value.replace(/[^\d+]/g, '')}` : `mailto:${value}`;
  }

  getPhoneEmailIcon(column: ICardSectionColumn): string {
    return this.isPhoneColumn(column) ? 'phone' : 'mail_outline';
  }

  addFieldRow(column: ICardSectionColumn): void {
    if (this.isPhoneColumn(column)) this.addPhoneRow();
    else this.addEmailRow();
  }

  removeFieldRow(column: ICardSectionColumn, index: number): void {
    if (this.isPhoneColumn(column)) this.removePhoneRow(index);
    else this.removeEmailRow(index);
  }

  get entityTitleKey(): 'leads' | 'deals' {
    return this.entityType === FUNNEL_TYPE.LEAD ? 'leads' : 'deals';
  }

  enterSectionEditMode(): void {
    if (!this.canEditSections || this.sectionEditMode) return;

    this.activeField = null;
    this.sectionDrafts = this.sections.map((section) => this.cloneSection(section));
    this.sectionDraftDirty = false;
    this.sectionEditMode = true;
  }

  cancelSectionEditMode(): void {
    if (this.savingSections) return;

    this.sectionDrafts = [];
    this.sectionDraftDirty = false;
    this.sectionEditMode = false;
  }

  addSection(): void {

    this.sectionDrafts = [
      ...this.sectionDrafts,
      {
        id: '',
        company_id: this.boardService.card.company_id,
        funnel_id: this.boardService.card.funnel_id,
        name: '',
        columns: [],
        position: this.sectionDrafts.length + 1,
        active: true,
        created_by: '',
        created_at: '',
      },
    ];
    this.sectionDraftDirty = true;
  }

  removeDraftSection(index: number): void {
    if (this.isMainSection(this.sectionDrafts[index])) return;

    this.sectionDrafts = this.sectionDrafts.filter((_, itemIndex) => itemIndex !== index);
    this.updateDraftPositions();
  }

  updateDraftSectionName(index: number, value: string): void {
    const section = this.sectionDrafts[index];
    if (!section) return;

    section.name = value;
    this.sectionDraftDirty = true;
  }

  isMainSection(section: ICardSection | undefined): boolean {
    return section?.name === 'General';
  }

  openSectionFieldsDialog(index: number): void {
    const section = this.sectionDrafts[index];
    if (!section) return;

    this.stackedSidebarService
      .openSidebar(
        {
          type: SidebarType.BoardV3SectionFields,
          width: '400px',
        },
        {
          funnelId: this.boardService.card.funnel_id,
          activeColumns: section.columns || [],
        },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        const columns = (result as { columns?: ICardSectionColumn[] } | undefined)?.columns;
        if (!Array.isArray(columns) || !this.sectionDrafts.includes(section)) return;

        section.columns = columns;
        this.sectionDraftDirty = true;
      });
  }

  removeDraftSectionColumn(sectionIndex: number, columnIndex: number): void {
    const section = this.sectionDrafts[sectionIndex];
    if (!section) return;

    section.columns = (section.columns || []).filter((_, itemIndex) => itemIndex !== columnIndex);
    this.sectionDraftDirty = true;
  }

  onDropDraftSection(event: CdkDragDrop<ICardSection[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    const mainSectionIndex = this.sectionDrafts.findIndex((section) => this.isMainSection(section));
    let targetIndex = event.currentIndex;
    if (mainSectionIndex !== -1) {
      if (event.previousIndex < mainSectionIndex && targetIndex >= mainSectionIndex) {
        targetIndex = mainSectionIndex - 1;
      } else if (event.previousIndex > mainSectionIndex && targetIndex <= mainSectionIndex) {
        targetIndex = mainSectionIndex + 1;
      }
    }
    if (event.previousIndex === targetIndex) return;

    moveItemInArray(this.sectionDrafts, event.previousIndex, targetIndex);
    this.updateDraftPositions();
  }

  onDropDraftSectionColumn(event: CdkDragDrop<ICardSectionColumn[]>, sectionIndex: number): void {
    const section = this.sectionDrafts[sectionIndex];
    if (!section?.columns || event.previousIndex === event.currentIndex) return;

    moveItemInArray(section.columns, event.previousIndex, event.currentIndex);
    this.sectionDraftDirty = true;
  }

  saveSectionChanges(closeAfterSave: boolean = false): void {
    if (this.savingSections) return;

    const invalidSection = this.sectionDrafts.find((section) => !section.name.trim());
    if (invalidSection) {
      this.notificationService.showError('', this.getTranslation('section_name_required'));
      return;
    }

    this.savingSections = true;
    const draftIds = new Set(this.sectionDrafts.map((section) => section.id).filter(Boolean));
    const sectionsToDelete = this.sections.filter((section) => !draftIds.has(section.id));
    const deleteRequests = sectionsToDelete.length
      ? forkJoin(sectionsToDelete.map((section) => this.boardService.deleteCardSection(section.id)))
      : of([]);
    const saveRequests = from(this.sectionDrafts).pipe(
      concatMap((section, index) => {
        const payload = {
          name: section.name.trim(),
          columns: section.columns || [],
          ...(this.isMainSection(section) ? {} : { position: index + 1 }),
        };

        return section.id
          ? this.boardService.updateCardSection(section.id, payload)
          : this.boardService.createCardSection({ ...payload, funnel_id: this.boardService.card.funnel_id });
      }),
      toArray(),
    );

    forkJoin({ deleted: deleteRequests, saved: saveRequests })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ saved }) => {
          this.applySections(saved as ICardSection[]);
          this.sectionDrafts = [];
          this.sectionDraftDirty = false;
          this.sectionEditMode = false;
          this.savingSections = false;
          this.notificationService.showSuccess('', this.getTranslation('sections_saved'));
          if (closeAfterSave) {
            this.showCloseConfirmation = false;
            this.close.emit({ card: this.boardService.card });
          }
        },
        error: () => {
          this.savingSections = false;
          this.loadSections();
          this.cancelSectionEditMode();
          this.notificationService.showError('', this.getTranslation('failed_to_save_sections'));
        },
      });
  }

  editField(field: EditableCardField): void {
    this.activeField = field;
    this.focusActiveControl();
  }

  isFieldEditing(field: EditableCardField): boolean {
    return this.activeField === field;
  }

  editSectionField(section: ICardSection, column: ICardSectionColumn): void {
    if (!this.isEditableSectionField(column)) return;

    this.activeField = this.getSectionEditKey(section.id, column.key);
    this.focusActiveControl();
  }

  @HostListener('document:click', ['$event'])
  closeActiveFieldOnOutsideClick(event: MouseEvent): void {
    if (!this.activeField) return;
    if (Date.now() < this.keepEditFocusUntil) return;

    const target = event.target as Node | null;
    if (!target) return;

    const targetElement = target instanceof Element ? target : target.parentElement;
    if (targetElement?.closest('[data-card-detail-edit-trigger="true"]')) return;
    if (this.isSelectOverlayClick(targetElement)) return;

    const isInsideActiveControl = this.editableControls
      ?.toArray()
      .some((control) => control.nativeElement.contains(target));
    if (isInsideActiveControl) return;

    this.activeField = null;
  }

  @HostListener('document:pointerdown', ['$event'])
  keepActiveFieldForDatePickerPointer(event: PointerEvent): void {
    if (!this.activeField) return;

    const target = event.target as Node | null;
    if (!target) return;

    const targetElement = target instanceof Element ? target : target.parentElement;
    if (this.isDatePickerOverlayClick(targetElement)) this.keepEditFocusForOverlay();
  }

  isSectionFieldEditing(section: ICardSection, column: ICardSectionColumn): boolean {
    return this.activeField === this.getSectionEditKey(section.id, column.key);
  }

  closeFieldOnFocusOut(event: FocusEvent): void {
    const currentTarget = event.currentTarget as HTMLElement | null;
    const nextTarget = event.relatedTarget as Node | null;

    if (currentTarget && nextTarget && currentTarget.contains(nextTarget)) {
      return;
    }
    if (this.shouldKeepEditFocus()) return;

    this.activeField = null;
  }

  keepEditFocusForSelect(): void {
    this.keepEditFocusForOverlay();
  }

  keepEditFocusForOverlay(): void {
    this.keepEditFocusUntil = Date.now() + 300;
  }

  onSectionFieldBlur(column: ICardSectionColumn, event: FocusEvent): void {
    const control = this.getSectionFieldControl(column);
    if (this.isDateField(column)) {
      control?.markAsTouched();
      this.keepEditFocusForOverlay();
      return;
    }

    if (typeof control?.value === 'string') {
      const value = control.value.trim();
      const inputType = this.fieldDefinitions.get(column.key)?.input_type;
      control.setValue(inputType ? normalizeCustomFieldValue(inputType, value) : value);
    }
    control?.markAsTouched();
    control?.updateValueAndValidity();
    this.closeFieldOnFocusOut(event);
  }

  onCustomSectionFieldBlur(column: ICardSectionColumn): void {
    const control = this.getSectionFieldControl(column);
    control?.markAsTouched();
    control?.updateValueAndValidity();
    this.activeField = null;
  }

  onSectionFieldInput(column: ICardSectionColumn, event: Event): void {
    const control = this.getSectionFieldControl(column);
    const input = event.target as HTMLInputElement;
    if (this.isCustomPhoneField(column)) {
      const value = sanitizePhoneInputValue(input.value);
      if (value === input.value) return;

      input.value = value;
      control?.setValue(value);
      return;
    }

    if (!this.isCustomPriceField(column)) return;

    const value = input.value.replace(/[^\d.,\s]/g, '');
    if (value === input.value) return;

    input.value = value;
    control?.setValue(value);
  }

  onPhoneInput(control: FormControl<string>, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = sanitizePhoneInputValue(input.value);
    if (value === input.value) return;

    input.value = value;
    control.setValue(value);
  }

  onSystemPriceInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/[^\d.,\s]/g, '');
    if (value === input.value) return;

    input.value = value;
    this.form.controls.price.setValue(value);
  }

  loadCard(): void {
    this.loading = true;
    this.loadError = false;

    this.boardService
      .getCard(this.cardId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (card) => {
          this.patchForm(card);
          this.subscribeToCardPresence(card);
          this.loadRelated(card);
        },
        error: () => {
          this.loading = false;
          this.loadError = true;
          this.boardService.resetCard();
          this.activeDetailTab = CardDetailTab.GENERAL;
          this.notificationService.showError('', this.getTranslation('failed_to_load_card'));
        },
      });
  }

  requestClose(): void {
    if (this.saving || this.savingSections || this.showCloseConfirmation) return;

    this.activeField = null;
    if (!this.hasChanges) {
      this.close.emit({ card: this.boardService.card });
      return;
    }

    this.showCloseConfirmation = true;
  }

  cancelClose(): void {
    this.showCloseConfirmation = false;
  }

  discardChangesAndClose(): void {
    if (this.saving || this.savingSections) return;

    this.showCloseConfirmation = false;
    this.close.emit({ card: this.boardService.card });
  }

  cancelChanges(): void {
    if (this.saving || this.savingSections) return;

    this.showCloseConfirmation = false;
    this.patchForm(this.boardService.card);
    this.patchSectionFields();
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.phonesArray.markAsPristine();
    this.phonesArray.markAsUntouched();
    this.emailsArray.markAsPristine();
    this.emailsArray.markAsUntouched();
    this.addressesControl.markAsPristine();
    this.addressesControl.markAsUntouched();
  }

  saveAndClose(): void {
    if (this.sectionEditMode) {
      this.saveSectionChanges(true);
      return;
    }

    this.save(true);
  }

  save(closeAfterSave: boolean = false): void {
    if (this.saving) return;

    this.form.markAllAsTouched();
    this.phonesArray.markAllAsTouched();
    // The editor's validator is detached once the field leaves edit mode, but the status is not recalculated
    this.addressesControl.updateValueAndValidity({ emitEvent: false });
    if (this.form.invalid || this.phonesArray.invalid || this.addressesControl.invalid) return;

    const payload = this.buildSavePayload();
    if (!Object.keys(payload).length) {
      if (closeAfterSave) {
        this.showCloseConfirmation = false;
        this.close.emit({ card: this.boardService.card });
      }
      return;
    }

    this.saving = true;
    this.boardService
      .updateCard(this.boardService.card.id, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (savedCard) => {
          this.patchForm(savedCard);
          this.patchSectionFields();
          this.saving = false;
          this.loadTimeline();
          this.notificationService.showSuccess('', this.getTranslation('card_saved'));

          if (closeAfterSave) {
            this.showCloseConfirmation = false;
            this.close.emit({ card: savedCard });
          }
        },
        error: (error) => {
          this.saving = false;
          const code = error?.error?.errors?.[0]?.code;
          const errorKeys: Record<string, string> = {
            CUSTOM_FIELD_VALUE_INVALID: 'errors.custom_field_value_invalid',
            ADDRESS_INVALID: 'board_v3_contact_error_address_invalid',
          };
          const key = errorKeys[code] ?? 'failed_to_save_card';
          this.notificationService.showError('', this.getTranslation(key));
        },
      });
  }

  getSectionFieldValue(column: ICardSectionColumn): string {
    const key = column.key;
    if (isUtmKey(key)) return this.formatDisplayValue(this.boardService.card[key]);

    const customValue = this.boardService.card.custom_fields?.[key];
    if (customValue !== undefined && customValue !== null && customValue !== '')
      return this.formatSectionFieldValue(column, customValue);

    const attributeValue = this.boardService.card.custom_attributes?.[key];
    if (attributeValue !== undefined && attributeValue !== null && attributeValue !== '')
      return this.formatSectionFieldValue(column, attributeValue);

    const knownValues: Record<string, unknown> = {
      phase: this.boardService.card.column?.name,
      name: this.boardService.card.name,
      price: formatMoneyAmount(this.boardService.card.price, this.boardService.card.currency),
      assigned_id: this.assignedManagerName,
      phone: this.formatPhones(this.boardService.card.phones),
      email: this.formatEmails(this.boardService.card.emails),
      description: this.boardService.card.description,
      created_at: this.boardService.card.created_at ? new Date(this.boardService.card.created_at).toLocaleDateString() : '',
      updated_at:
        this.boardService.card.updated_at || this.boardService.card.created_at
          ? new Date(this.boardService.card.updated_at || this.boardService.card.created_at).toLocaleDateString()
          : '',
      updated_by: this.getManagerName(this.boardService.card.updated_by),
      currency: this.boardService.card.currency,
    };

    const value = knownValues[key];
    return this.formatDisplayValue(value);
  }

  getSectionPhoneLink(column: ICardSectionColumn): string {
    const value = this.boardService.card.custom_fields?.[column.key] ?? this.boardService.card.custom_attributes?.[column.key] ?? '';
    return `tel:${String(value).replace(/[^\d+]/g, '')}`;
  }

  getEditableFieldValue(field: EditableCardField): string {
    switch (field) {
      case 'name':
        return this.formatDisplayValue(this.form.controls.name.value);
      case 'price': {
        const price = this.form.controls.price.value;
        const currency = this.form.controls.currency.value;

        return formatMoneyAmount(price, currency);
      }
      case 'assigned_id':
        return this.getManagerName(this.form.controls.assigned_id.value);
      case 'description':
        return this.formatDisplayValue(this.form.controls.description.value);
      case 'phones':
        return this.formatDisplayValue(this.form.controls.phones.value);
      case 'emails':
        return this.formatDisplayValue(this.form.controls.emails.value);
    }
  }

  getEditableSectionFieldValue(column: ICardSectionColumn): string {
    if (column.key === 'price')
      return formatMoneyAmount(this.form.controls.price.value, this.form.controls.currency.value);

    const systemControl = this.getSystemSectionFieldControl(column.key);
    if (systemControl) {
      if (column.key === 'assigned_id') return this.getManagerName(systemControl.value as string | number | null);
      return this.formatSectionFieldValue(column, systemControl.value);
    }

    const control = this.form.controls.section_fields.controls[column.key];
    if (control) {
      if (column.key === 'assigned_id') return this.getManagerName(control.value as string | number | null);
      if (this.isDateField(column)) {
        return this.formatSectionFieldValue(
          column,
          this.normalizeDateValue(control.value, this.getSectionDateTimeControl(column)?.value ?? ''),
        );
      }
      return this.formatSectionFieldValue(column, control.value);
    }

    return this.getSectionFieldValue(column);
  }

  getSectionFieldControl(column: ICardSectionColumn): FormControl<unknown> {
    const systemControl = this.getSystemSectionFieldControl(column.key);
    if (systemControl) return systemControl;

    return this.form.controls.section_fields.controls[column.key] as FormControl<unknown>;
  }

  getSectionDateTimeControl(column: ICardSectionColumn): FormControl<unknown> {
    return this.form.controls.section_fields.controls[this.getDateTimeControlKey(column.key)] as FormControl<unknown>;
  }

  onSectionDateChange(column: ICardSectionColumn): void {
    const dateControl = this.getSectionFieldControl(column);
    const timeControl = this.getSectionDateTimeControl(column);
    if (!dateControl?.value && timeControl?.value) timeControl.setValue('');
  }

  onSectionSelectChange(column: ICardSectionColumn): void {
    const control = this.getSectionFieldControl(column);
    control?.markAsTouched();
  }

  hasSectionFieldError(column: ICardSectionColumn): boolean {
    if (column.key === 'price') return this.form.controls.price.invalid && this.form.controls.price.touched;
    const control = this.getSectionFieldControl(column);
    return !!control?.invalid && control.touched;
  }

  getSectionFieldError(column: ICardSectionColumn): string {
    if (column.key === 'price') return this.getTranslation('input_errors.price');
    const errors = this.getSectionFieldControl(column)?.errors;
    if (errors?.['maxlength'])
      return this.getTranslation('input_errors.maxlength').replace(
        '{{value}}',
        String(this.getSectionFieldMaxLength(column)),
      );
    if (errors?.['singleLine']) return this.getTranslation('input_errors.single_line');
    if (errors?.['number']) return this.getTranslation('input_errors.number');
    if (errors?.['price']) return this.getTranslation('input_errors.price');
    if (errors?.['currency']) return this.getTranslation('input_errors.currency');
    if (errors?.['phone']) return this.getTranslation('input_errors.phone');
    if (errors?.['email']) return this.getTranslation('input_errors.email');
    if (errors?.['link']) return this.getTranslation('input_errors.link');
    return this.getTranslation('input_errors.required');
  }

  getSectionFieldMaxLength(column: ICardSectionColumn): number {
    const field = this.fieldDefinitions.get(column.key);
    return field ? getCustomFieldMaxLength(field.input_type) : 256;
  }

  getSectionFieldValueLength(column: ICardSectionColumn): number {
    const value = this.getSectionFieldControl(column)?.value;
    if (value === null || value === undefined) return 0;
    if (typeof value === 'string') return value.length;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value).length;
    return 0;
  }

  isEditableSectionField(column: ICardSectionColumn): boolean {
    // An amount calculated from the product items is not typed by hand
    if (column.key === 'price' && this.boardService.card?.is_price_from_items) return false;

    return !column.readonly && !this.readonlySectionKeys.has(column.key);
  }

  getVisibleSectionColumns(section: ICardSection): ICardSectionColumn[] {
    return (section.columns || []).filter((column) => this.isVisibleSectionColumn(column));
  }

  private isVisibleSectionColumn(column: ICardSectionColumn): boolean {
    if (column.key === 'phase') return false;
    if (column.key === SECTION_CLIENTS_KEY) return true;

    const definition = this.fieldDefinitions.get(column.key);
    if (definition) return true;

    const key = this.normalizeFieldKey(column.key);
    const title = this.normalizeFieldKey(column.title);
    if (!this.systemSectionKeys.has(key)) return false;

    return column.readonly === true || this.readonlySectionKeys.has(key) || !title || title === key;
  }

  getSectionInputType(column: ICardSectionColumn): string {
    if (this.isCustomNumberField(column) || this.isCustomPriceField(column)) return 'text';
    if (this.isCustomPhoneField(column)) return 'tel';
    if (this.isCustomEmailField(column)) return 'email';
    if (this.isCustomLinkField(column)) return 'url';
    return column.key === 'price' || column.type === 'number' ? 'number' : 'text';
  }

  isTextareaField(column: ICardSectionColumn): boolean {
    const field = this.fieldDefinitions.get(column.key);
    return column.type === 'textarea' || (!!field && !field.is_system && field.input_type === 'textarea');
  }

  isDateField(column: ICardSectionColumn): boolean {
    return column.type === 'date' || this.isCustomDateField(column);
  }

  getCustomField(column: ICardSectionColumn): BoardField | undefined {
    const field = this.fieldDefinitions.get(column.key);
    return field && !field.is_system ? field : undefined;
  }

  isEditableCustomField(column: ICardSectionColumn): boolean {
    return !!this.getCustomField(column);
  }

  isCustomNumberField(column: ICardSectionColumn): boolean {
    const field = this.fieldDefinitions.get(column.key);
    return !!field && !field.is_system && field.input_type === 'number';
  }

  isCustomPriceField(column: ICardSectionColumn): boolean {
    const field = this.fieldDefinitions.get(column.key);
    return !!field && !field.is_system && field.input_type === 'price';
  }

  isCustomPhoneField(column: ICardSectionColumn): boolean {
    const field = this.fieldDefinitions.get(column.key);
    return !!field && !field.is_system && field.input_type === 'phone';
  }

  isCustomEmailField(column: ICardSectionColumn): boolean {
    const field = this.fieldDefinitions.get(column.key);
    return !!field && !field.is_system && field.input_type === 'email';
  }

  isCustomLinkField(column: ICardSectionColumn): boolean {
    const field = this.fieldDefinitions.get(column.key);
    return !!field && !field.is_system && field.input_type === 'link';
  }

  isCustomDateField(column: ICardSectionColumn): boolean {
    const field = this.fieldDefinitions.get(column.key);
    return !!field && !field.is_system && field.input_type === 'date';
  }

  isCustomSelectField(column: ICardSectionColumn): boolean {
    const field = this.fieldDefinitions.get(column.key);
    return !!field && !field.is_system && field.input_type === 'select';
  }

  isCheckboxField(column: ICardSectionColumn): boolean {
    const field = this.fieldDefinitions.get(column.key);
    return column.type === 'checkbox' || field?.input_type === 'checkbox';
  }

  getCustomSelectOptions(column: ICardSectionColumn): Array<{ label: string; value: string }> {
    const options = this.fieldDefinitions.get(column.key)?.options ?? [];
    return options.map((value) => ({ label: value, value }));
  }

  getSectionFieldLabel(column: ICardSectionColumn): string {
    const labels: Record<string, string> = {
      phase: 'stage',
      assigned_id: 'custom_filters.assigned_id',
      updated_by: 'updated_by',
      [SECTION_CLIENTS_KEY]: 'board_v3_quick_create_clients',
    };

    const labelKey = labels[column.key] || column.key;
    const translated = this.translateService.instant(labelKey);

    if (translated && translated !== labelKey) {
      return translated as string;
    }

    const liveFieldName = this.fieldDefinitions.get(column.key)?.name;
    if (liveFieldName) {
      return liveFieldName;
    }

    return column.title || column.key;
  }

  get selectedStage(): BoardColumn | undefined {
    return this.columns.find((column) => column.id === this.boardService.card.column_id);
  }

  get currentStagePosition(): number {
    return this.selectedStage?.position ?? this.boardService.card.column?.position ?? 0;
  }

  isStageReached(column: BoardColumn): boolean {
    return column.position <= this.currentStagePosition;
  }

  onSelectStage(column: BoardColumn): void {
    if (this.movingStage || column.id === this.boardService.card.column_id) return;

    const card = this.boardService.card;
    const previousColumnId = card.column_id;
    const previousColumn = card.column;
    this.movingStage = true;
    card.column_id = column.id;

    this.boardService
      .moveCard(card.id, { column_id: column.id, top_card_id: null, bottom_card_id: null, funnel_id: card.funnel_id })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.movingStage = false)),
      )
      .subscribe({
        next: (updatedCard) => {
          card.column_id = updatedCard.column_id ?? column.id;
          card.position = updatedCard.position ?? card.position;
          this.loadTimeline();
          // The new column may reserve or write the product items off
          if (card.items?.length) this.reloadItems();
        },
        error: (error) => {
          card.column_id = previousColumnId;
          card.column = previousColumn;
          this.showStageError(error);
        },
      });
  }

  onItemsChange(block: CardItemsBlock): void {
    const card = this.boardService.card;
    if (!card) return;

    card.items = block.items;
    card.items_totals = block.totals;
    card.items_written_off = block.written_off;
    // The backend recalculates the amount as well and sends it with the card update event
    if (card.is_price_from_items) {
      this.applyCardPrice(block.totals.find((total) => total.currency === card.currency)?.amount ?? 0);
    }
  }

  /** The checkbox of the products tab is saved right away; the backend answers with the recalculated amount */
  onPriceModeChange(change: Pick<ICardV2, 'is_price_from_items' | 'price'>): void {
    if (!this.boardService.card) return;

    this.boardService.card.is_price_from_items = change.is_price_from_items;
    this.applyCardPrice(change.price);
  }

  private applyCardPrice(price: number): void {
    const card = this.boardService.card;
    const changed = card.price !== price;
    card.price = price;
    if (!this.form.controls.price.dirty) this.form.controls.price.setValue(price, { emitEvent: false });
    // The backend derives the payment status from the amount: ask for it again unless nothing is paid (always unpaid)
    if (changed && card.paid_total) this.reloadPayments();
  }

  private reloadItems(): void {
    const cardId = this.cardId;
    this.warehouseService
      .getCardItems(cardId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (block) => {
          if (this.boardService.card?.id === cardId) this.onItemsChange(block);
        },
      });
  }

  /** Payments are saved right away; the backend derives the paid total and the payment status from them */
  onPaymentsChange(block: CardPaymentsBlock): void {
    const card = this.boardService.card;
    if (!card) return;

    card.payments = block.payments;
    card.paid_total = block.paid_total;
    card.payment_status = block.payment_status;
  }

  private reloadPayments(): void {
    const cardId = this.cardId;
    this.paymentService
      .getCardPayments(cardId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (block) => {
          if (this.boardService.card?.id === cardId) this.onPaymentsChange(block);
        },
        error: (error) => this.paymentService.error(error),
      });
  }

  /** Missing stock or a product item without a warehouse keep the card in its column (409) */
  private showStageError(error: unknown): void {
    const code = getApiError(error).code;
    if (code !== 'INSUFFICIENT_STOCK' && code !== 'CARD_ITEM_WAREHOUSE_REQUIRED') {
      this.notificationService.showError('', this.getTranslation('failed_to_save_card'));
      return;
    }

    const items = this.boardService.card?.items ?? [];
    this.warehouseService.error(
      error,
      (variantId) => getVariantTitle(items.find((item) => item.variant_id === variantId)?.variant) || null,
    );
  }

  get assignedManagerName(): string {
    return this.getManagerName(this.boardService.card.assigned_id);
  }

  onContactsChange(contacts: BoardContact[]): void {
    this.boardService.card.contacts = contacts;
    this.loadTimeline();
  }

  /** Keeps the card contacts in sync with a contact changed from the timeline */
  onTimelineContactChanged({ contactId, result }: TimelineContactChange): void {
    const contacts = this.boardService.card.contacts ?? [];
    const stillLinked = !result.deleted && (result.contact?.cards?.some((card) => card.id === this.cardId) ?? true);

    if (!stillLinked) {
      this.onContactsChange(contacts.filter((item) => item.id !== contactId));
      return;
    }
    if (!result.contact) return;

    const updated = result.contact;
    const exists = contacts.some((item) => item.id === contactId);
    this.onContactsChange(
      exists ? contacts.map((item) => (item.id === contactId ? updated : item)) : [...contacts, updated],
    );
  }

  onCompanyChange(company: BoardCompany | null): void {
    this.boardService.card.company = company;
    this.loadTimeline();
  }

  /** Keeps the card company in sync with a company changed from the timeline */
  onTimelineCompanyChanged({ companyV2Id, result }: TimelineCompanyChange): void {
    const isCurrent = this.boardService.card.company?.id === companyV2Id;
    const stillLinked = !result.deleted && (result.company?.cards?.some((card) => card.id === this.cardId) ?? true);

    if (!stillLinked) {
      if (isCurrent) this.onCompanyChange(null);
      return;
    }
    if (result.company) this.onCompanyChange(result.company);
  }

  getCardDisplayId(card: ICardV2): string {
    return String((card as ICardV2 & { rn?: string | number }).rn || card.id);
  }

  private loadRelated(card: ICardV2): void {
    forkJoin({
      sections: this.boardService.getCardSections(card.funnel_id).pipe(catchError(() => of([]))),
      managers: this.boardService.getManagers({ page: 1, take: 500 }).pipe(catchError(() => of([]))),
      companies: this.authService.getCompanies().pipe(catchError(() => of({ data: [] }))),
      boardFields: this.boardService.getBoardFields(card.funnel_id, 'FUNNEL').pipe(catchError(() => of([]))),
      columns: this.boardService.getAllColumns(card.funnel_id).pipe(catchError(() => of([]))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ sections, managers, companies, boardFields, columns }) => {
        this.fieldDefinitions = new Map(boardFields.map((field: BoardField) => [field.identifier, field]));
        this.columns = (columns || []).slice().sort((a, b) => a.position - b.position);
        this.applySections(sections || []);
        // WTF?
        this.canEditSections = (companies?.data || []).some(
          (company: { id: string; role: string }) =>
            String(company.id) === String(card.company_id) && company.role === 'OWNER',
        );
        this.managers = (managers || []) as unknown as CardManager[];
        this.patchSectionFields();
        this.loading = false;
      });
  }

  /** After a change of the card its timeline is loaded again */
  loadTimeline(): void {
    this.timeline?.reload();
  }

  private loadSections(): void {

    this.boardService
      .getCardSections(this.boardService.card.funnel_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((sections) => this.applySections(sections || []));
  }

  private applySections(sections: ICardSection[]): void {
    this.sections = sections.map((section) => ({
      ...this.cloneSection(section),
      columns: this.getVisibleSectionColumns(section),
    }));
    this.visibleSections = this.sections
      .map((section) => ({ ...section, visibleColumns: this.getVisibleSectionColumns(section) }))
      .filter((section) => section.visibleColumns.length > 0);
    if (this.boardService.card) this.boardService.card.sections = this.sections;
    this.patchSectionFields();
  }

  private updateDraftPositions(): void {
    this.sectionDrafts.forEach((section, index) => {
      if (!this.isMainSection(section)) section.position = index + 1;
    });
    this.sectionDraftDirty = true;
  }

  private cloneSection(section: ICardSection): ICardSection {
    return { ...section, columns: (section.columns || []).map((column) => ({ ...column })) };
  }

  private subscribeToCardDetailUpdates(cardId: string): void {
    this.detailUpdateSubscription?.unsubscribe();
    this.detailUpdateSubscription = this.realtimeService
      .cardDetailUpdated$(cardId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ card }) => {
        this.applyCardDetailUpdate(card);
      });
  }

  private applyCardDetailUpdate(card: ICardV2): void {
    if (this.cardId !== card.id || this.saving || this.savingSections) return;

    const previousCard = this.boardService.card;
    this.boardService.card = { ...card, sections: this.sections };
    this.boardService.isCardLoaded = true;
    this.generalPanel?.reloadChats();

    if (this.hasChanges || this.sectionEditMode) {
      const changes = this.describeCardChanges(previousCard, card);
      // Contact, company, product item and payment changes are saved right away and never clash with the unsaved form;
      // the paid total and the payment status follow from the payments and the amount, so they are not compared
      const linksChanged =
        !this.isSameValue(previousCard?.contacts, card.contacts) ||
        !this.isSameValue(previousCard?.company, card.company) ||
        !this.isSameValue(previousCard?.items, card.items) ||
        !this.isSameValue(previousCard?.items_written_off, card.items_written_off) ||
        !this.isSameValue(previousCard?.payments, card.payments);
      if (!changes.length && linksChanged) return;

      this.conflict = {
        authorName: this.getManagerName(card.updated_by),
        changes,
      };
      return;
    }

    this.patchForm(this.boardService.card);
    this.patchSectionFields();
  }

  discardMyChanges(): void {
    this.conflict = null;
    this.activeField = null;
    this.sectionEditMode = false;
    this.sectionDraftDirty = false;
    this.sectionDrafts = [];
    this.cancelChanges();
  }

  keepMyChanges(): void {
    this.conflict = null;
  }

  private describeCardChanges(previous: ICardV2 | null, next: ICardV2): string[] {
    if (!previous) return [];

    const changes: string[] = [];
    const systemKeys: Array<keyof ICardV2> = [
      'name',
      'price',
      'currency',
      'description',
      'assigned_id',
      'column_id',
      'comment',
      ...UTM_KEYS,
    ];
    for (const key of systemKeys) {
      // An amount calculated from the product items changes with them, it is not someone else's edit
      if (key === 'price' && next.is_price_from_items) continue;
      if (!this.isSameValue(previous[key], next[key])) changes.push(this.getFieldLabel(String(key)));
    }

    if (!this.isSameValue(previous.phones?.map(getCardPhoneValue), next.phones?.map(getCardPhoneValue))) {
      changes.push(this.getFieldLabel('phones'));
    }
    if (!this.isSameValue(previous.emails?.map((item) => item.email), next.emails?.map((item) => item.email))) {
      changes.push(this.getFieldLabel('emails'));
    }
    if (!isSameAddressList(previous.addresses, next.addresses)) {
      changes.push(this.getFieldLabel('addresses'));
    }
    if (!this.isSameValue((previous.chats ?? []).map((item) => item.id), (next.chats ?? []).map((item) => item.id))) {
      changes.push(this.getFieldLabel('chats'));
    }

    const customKeys = new Set([
      ...Object.keys(previous.custom_fields ?? {}),
      ...Object.keys(next.custom_fields ?? {}),
      ...Object.keys(previous.custom_attributes ?? {}),
      ...Object.keys(next.custom_attributes ?? {}),
    ]);
    for (const key of customKeys) {
      const previousValue = previous.custom_fields?.[key] ?? previous.custom_attributes?.[key];
      const nextValue = next.custom_fields?.[key] ?? next.custom_attributes?.[key];
      if (!this.isSameValue(previousValue, nextValue)) changes.push(this.getFieldLabel(key));
    }

    return [...new Set(changes)];
  }

  private isSameValue(a: unknown, b: unknown): boolean {
    return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  }

  private getFieldLabel(key: string): string {
    const labelKeys: Record<string, string> = {
      name: 'custom_filters.name',
      price: 'custom_filters.price',
      currency: 'board_v3_field_currency',
      description: 'description',
      assigned_id: 'custom_filters.assigned_id',
      column_id: 'phase',
      comment: 'custom_filters.comment',
      phones: 'phones',
      emails: 'emails',
      addresses: 'addresses',
      chats: 'chats',
    };

    const fieldName = this.fieldDefinitions.get(key)?.name;
    if (!labelKeys[key] && fieldName) return fieldName;

    const translated = this.translateService.instant(labelKeys[key] || key);
    return typeof translated === 'string' && translated !== labelKeys[key] ? translated : fieldName || key;
  }

  private subscribeToCardPresence(card: ICardV2): void {
    const companyId = String(this.baseService.getCompanyObj()?.id || card.company_id || '');
    if (!companyId || !card.funnel_id) return;
    if (
      this.presenceCardId === card.id &&
      this.presenceCompanyId === companyId &&
      this.presenceFunnelId === card.funnel_id
    ) {
      return;
    }

    this.unsubscribeFromCardPresence();
    this.presenceCardId = card.id;
    this.presenceCompanyId = companyId;
    this.presenceFunnelId = card.funnel_id;
    const currentViewer = this.getCurrentViewer();
    this.viewers = currentViewer ? [currentViewer] : [];
    this.presenceSubscription = this.realtimeService
      .cardPresence$(card.funnel_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.card_id !== card.id) return;
        this.viewers = event.viewers ?? [];
      });
    this.realtimeService.subscribeCard(card.id, companyId, card.funnel_id);
    this.startPresenceHeartbeat(card.id, companyId, card.funnel_id);
  }

  private unsubscribeFromCardPresence(): void {
    if (this.presenceCardId && this.presenceCompanyId) {
      this.realtimeService.unsubscribeCard(this.presenceCardId, this.presenceCompanyId, this.presenceFunnelId);
    }

    this.presenceHeartbeatSubscription?.unsubscribe();
    this.presenceHeartbeatSubscription = undefined;
    this.presenceSubscription?.unsubscribe();
    this.presenceSubscription = undefined;
    this.presenceCardId = undefined;
    this.presenceCompanyId = undefined;
    this.presenceFunnelId = undefined;
  }

  private startPresenceHeartbeat(cardId: string, companyId: string, funnelId?: string): void {
    this.presenceHeartbeatSubscription?.unsubscribe();
    this.presenceHeartbeatSubscription = this.presenceService
      .cardHeartbeat$(
        cardId,
        companyId,
        funnelId,
        () => this.presenceCardId === cardId && this.presenceCompanyId === companyId,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private getCurrentViewer(): CardViewer | null {
    const user = this.baseService.currentUserInfo || this.baseService.user;
    if (!user?.id) return null;

    return {
      id: String(user.id),
      first_name: user.first_name ?? '',
      last_name: user.last_name ?? '',
      avatar_url: user.avatar_url ?? null,
      email: user.email ?? '',
    };
  }

  private patchForm(card: ICardV2): void {
    this.activeField = null;
    this.form.reset(
      {
        name: card.name,
        price: card.price,
        currency: card.currency,
        description: card.description,
        assigned_id: card.assigned_id,
        phones: this.formatPhones(card.phones, ''),
        emails: this.formatEmails(card.emails, ''),
        section_fields: {},
      },
      { emitEvent: false },
    );
  }

  private patchSectionFields(): void {
    this.phonesArray = this.buildPhoneFormArray(this.cardPhones.map(getCardPhoneValue));
    this.emailsArray = this.buildStringFormArray(this.cardEmails.map((item) => item.email));
    this.addressesControl.reset(this.cardAddresses.map(toAddressInput));

    const group = new FormGroup<Record<string, FormControl<unknown>>>({});
    for (const section of this.visibleSections) {
      for (const column of section.visibleColumns) {
        if (this.isPhoneColumn(column) || this.isEmailColumn(column) || this.isAddressColumn(column)) continue;

        if (this.isEditableSectionField(column)) {
          if (this.getSystemSectionFieldControl(column.key)) continue;

          const field = this.fieldDefinitions.get(column.key);
          const dateParts = this.isDateField(column)
            ? this.getDateParts(this.getRawSectionFieldValue(column.key))
            : null;
          const validators = field ? getCustomFieldValidators(field.input_type, field.is_required) : [];
          group.addControl(
            column.key,
            new FormControl(dateParts?.date ?? this.getRawSectionFieldValue(column.key), validators),
          );
          if (dateParts) {
            group.addControl(this.getDateTimeControlKey(column.key), new FormControl(dateParts.time));
          }
        }
      }
    }

    this.form.setControl('section_fields', group);
  }

  private buildStringFormArray(values: string[]): FormArray<FormControl<string>> {
    const nonEmpty = values.filter(Boolean);
    const seedValues = nonEmpty.length ? nonEmpty : [''];

    return new FormArray(seedValues.map((value) => new FormControl(value, { nonNullable: true })));
  }

  private buildPhoneFormArray(values: string[]): FormArray<FormControl<string>> {
    const nonEmpty = values.filter(Boolean);
    const seedValues = nonEmpty.length ? nonEmpty : [''];

    return new FormArray(
      seedValues.map((value) => new FormControl(value, { nonNullable: true, validators: [customFieldPhoneValidator] })),
    );
  }

  private buildSavePayload(): Record<string, unknown> {
    const raw = this.form.getRawValue();
    const controls = this.form.controls;
    const payload: Record<string, unknown> = {};

    if (controls.name.dirty) payload['name'] = raw.name ?? '';
    if (controls.price.dirty) payload['price'] = raw.price ?? 0;
    if (controls.currency.dirty) payload['currency'] = raw.currency ?? '';
    if (controls.description.dirty) payload['description'] = raw.description ?? '';
    if (controls.assigned_id.dirty) payload['assigned_id'] = raw.assigned_id || null;
    if (this.phonesArray.dirty) payload['phones'] = this.getNonEmptyPhoneValues();
    if (this.emailsArray.dirty) payload['emails'] = this.getNonEmptyArrayValues(this.emailsArray);
    if (this.addressesControl.dirty) payload['addresses'] = this.addressesControl.value;

    const customFields = this.applyDirtySectionFields(payload);
    if (Object.keys(customFields).length) {
      payload['custom_fields'] = customFields;
    }

    return payload;
  }

  private getNonEmptyArrayValues(array: FormArray<FormControl<string>>): string[] {
    return array.controls.map((control) => control.value.trim()).filter(Boolean);
  }

  private getNonEmptyPhoneValues(): string[] {
    return this.phonesArray.controls.map((control) => normalizePhonePayloadValue(control.value)).filter(Boolean);
  }

  private applyDirtySectionFields(payload: Record<string, unknown>): Record<string, unknown> {
    const customFields: Record<string, unknown> = {};
    const controls = this.form.controls.section_fields.controls;
    const processedDateFields = new Set<string>();

    Object.entries(controls).forEach(([key, control]) => {
      if (key.endsWith('__time')) return;

      const definition = this.fieldDefinitions.get(key);
      if (definition?.input_type === 'date' || controls[this.getDateTimeControlKey(key)]) {
        if (processedDateFields.has(key)) return;
        processedDateFields.add(key);
        const timeControl = controls[this.getDateTimeControlKey(key)];
        if (control.dirty || timeControl?.dirty) {
          customFields[key] = this.normalizeDateValue(control.value, timeControl?.value ?? '');
        }
        return;
      }

      if (!control.dirty) return;

      const value = control.value ?? '';
      if (isUtmKey(key)) {
        payload[key] = this.stringifyValue(value).trim() || null;
        return;
      }

      switch (key) {
        case 'name':
          payload['name'] = this.stringifyValue(value);
          break;
        case 'price':
          payload['price'] = Number(value) || 0;
          break;
        case 'assigned_id':
          payload['assigned_id'] = this.stringifyValue(value) || null;
          break;
        case 'description':
          payload['description'] = this.stringifyValue(value);
          break;
        case 'currency':
          payload['currency'] = this.stringifyValue(value);
          break;
        default:
          customFields[key] = definition ? normalizeCustomFieldValue(definition.input_type, value) : value;
      }
    });

    return customFields;
  }

  private getRawSectionFieldValue(key: string): unknown {
    if (isUtmKey(key)) return this.boardService.card[key] ?? '';

    switch (key) {
      case 'name':
        return this.boardService.card.name ?? '';
      case 'price':
        return this.boardService.card.price ?? 0;
      case 'assigned_id':
        return this.boardService.card.assigned_id ?? null;
      case 'description':
        return this.boardService.card.description ?? '';
      case 'currency':
        return this.boardService.card.currency ?? '';
    }

    const customValue = this.boardService.card.custom_fields?.[key];
    if (customValue !== undefined && customValue !== null) return customValue;

    const attributeValue = this.boardService.card.custom_attributes?.[key];
    if (attributeValue !== undefined && attributeValue !== null) return attributeValue;

    return '';
  }

  private getSystemSectionFieldControl(key: string): FormControl<unknown> | null {
    switch (key) {
      case 'name':
        return this.form.controls.name as unknown as FormControl<unknown>;
      case 'price':
        return this.form.controls.price as unknown as FormControl<unknown>;
      case 'assigned_id':
        return this.form.controls.assigned_id as unknown as FormControl<unknown>;
      case 'description':
        return this.form.controls.description as unknown as FormControl<unknown>;
      case 'currency':
        return this.form.controls.currency as unknown as FormControl<unknown>;
      default:
        return null;
    }
  }

  private getSectionEditKey(sectionId: string, key: string): string {
    return `section:${sectionId}:${key}`;
  }

  private focusActiveControl(): void {
    setTimeout(() => {
      const root = this.editableControls?.first?.nativeElement;
      const control = this.getFocusableControl(root);
      control?.focus();

      if (control instanceof HTMLInputElement) {
        control.select();
      } else if (control instanceof HTMLTextAreaElement) {
        control.setSelectionRange(control.value.length, control.value.length);
      }
    });
  }

  private getFocusableControl(
    root: HTMLElement | undefined,
  ): HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null {
    if (!root) return null;
    if (root.hasAttribute('data-card-detail-skip-auto-focus')) return null;
    if (root instanceof HTMLInputElement || root instanceof HTMLSelectElement || root instanceof HTMLTextAreaElement)
      return root;

    return root.querySelector('input, select, textarea');
  }

  private isSelectOverlayClick(target: Element | null): boolean {
    return (
      !!target?.closest(
        '.p-select-overlay, .p-autocomplete-overlay, .p-dropdown-panel, .p-multiselect-panel, .pac-container',
      ) ||
      this.isDatePickerOverlayClick(target)
    );
  }

  private isDatePickerOverlayClick(target: Element | null): boolean {
    return !!target?.closest('.p-datepicker-panel, .board-v3-datepicker-panel');
  }

  private isDatePickerInteraction(target: Element | null): boolean {
    return !!target?.closest('.p-datepicker, .p-datepicker-panel, .board-v3-datepicker-panel');
  }

  private shouldKeepEditFocus(): boolean {
    if (Date.now() < this.keepEditFocusUntil) return true;
    const activeElement = document.activeElement;
    if (
      activeElement instanceof Element &&
      (activeElement.closest(
        '.p-select, .p-select-overlay, .p-autocomplete-overlay, .p-dropdown-panel, .p-multiselect-panel',
      ) ||
        this.isDatePickerInteraction(activeElement))
    ) {
      return true;
    }
    return !!document.querySelector(
      '.p-select-overlay, .p-autocomplete-overlay, .p-dropdown-panel, .p-multiselect-panel, .p-datepicker-panel, .board-v3-datepicker-panel',
    );
  }

  private getManagerName(managerId: string | number | null | undefined): string {
    const assigned = this.boardService.card.assigned;
    const manager =
      assigned && String(assigned.id) === String(managerId ?? '')
        ? assigned
        : this.managers.find((item) => String(item.id) === String(managerId ?? ''));
    if (!manager) return '-';

    return formatFullName(manager.first_name, manager.last_name) || manager.email || '-';
  }

  get managersOptions(): Array<{ id: string | number; name: string; avatarUrl?: string }> {
    return this.managers.map((manager) => ({
      id: manager.id,
      name: formatFullName(manager.first_name, manager.last_name) || manager.email || '-',
      avatarUrl: manager.avatar_url || undefined,
    }));
  }

  getAssignedManagerAvatarUrl(column: ICardSectionColumn): string | undefined {
    const managerId = this.getSectionFieldControl(column)?.value as string | number | null;
    return this.getManagerAvatarUrlById(managerId);
  }

  getUpdatedByAvatarUrl(): string | undefined {
    return this.getManagerAvatarUrlById(this.boardService.card.updated_by ?? null);
  }

  private getManagerAvatarUrlById(managerId: string | number | null): string | undefined {
    const assigned = this.boardService.card.assigned;
    if (assigned && String(assigned.id) === String(managerId ?? '')) return assigned.avatar_url || undefined;

    const manager = this.managers.find((item) => String(item.id) === String(managerId ?? ''));
    return manager?.avatar_url || undefined;
  }

  private getTranslation(key: string): string {
    const value = this.translateService.instant(key);

    return typeof value === 'string' ? value : key;
  }

  private formatDisplayValue(value: unknown): string {
    const text = this.stringifyValue(value).trim();

    return text || '-';
  }

  private formatSectionFieldValue(column: ICardSectionColumn, value: unknown): string {
    if (this.isCustomPriceField(column)) return this.formatCustomPriceValue(value);
    if (this.isCustomPhoneField(column)) return this.formatCustomPhoneValue(value);
    if (this.isDateField(column)) return this.formatCustomDateValue(value);
    if (!this.isCustomNumberField(column)) return this.formatDisplayValue(value);

    const [integerPart, fractionPart] = String(value ?? '')
      .trim()
      .split('.');
    if (!/^-?\d+$/.test(integerPart || '')) return this.formatDisplayValue(value);
    const sign = integerPart.startsWith('-') ? '-' : '';
    const groupedInteger = integerPart.replace(/^-/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${sign}${groupedInteger}${fractionPart !== undefined ? `.${fractionPart}` : ''}`;
  }

  private formatCustomDateValue(value: unknown): string {
    if (typeof value !== 'string' || !value) return '-';
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-');
      return `${day}.${month}.${year}`;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return this.formatDisplayValue(value);
    return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }

  private getDateParts(value: unknown): { date: string; time: string } {
    if (typeof value !== 'string' || !value) return { date: '', time: '' };
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return { date: value, time: '' };

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return { date: '', time: '' };
    return {
      date: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`,
      time: `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`,
    };
  }

  private normalizeDateValue(dateValue: unknown, timeValue: unknown): string {
    if (typeof dateValue !== 'string' || !dateValue) return '';
    if (typeof timeValue !== 'string' || !timeValue) return dateValue;

    const [year, month, day] = dateValue.split('-').map(Number);
    const [hours, minutes] = timeValue.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes).toISOString();
  }

  private getDateTimeControlKey(key: string): string {
    return `${key}__time`;
  }

  private formatCustomPriceValue(value: unknown): string {
    if (!isPriceFieldValue(value)) return this.formatDisplayValue(value);
    return formatPriceFieldValue(value);
  }

  private normalizePhoneValue(value: unknown): string {
    return normalizePhonePayloadValue(value);
  }

  private normalizeLinkValue(value?: string): string {
    const source = String(value ?? '').trim();
    if (!source || /\s/.test(source)) return source;
    return /^[a-z][a-z\d+.-]*:\/\//i.test(source) ? source : `https://${source}`;
  }

  getSectionLink(column: ICardSectionColumn): string {
    const value = this.boardService.card.custom_fields?.[column.key] ?? this.boardService.card.custom_attributes?.[column.key] ?? '';
    return this.normalizeLinkValue(value);
  }

  private formatCustomPhoneValue(value: unknown): string {
    return this.formatPhoneValue(value);
  }

  private formatPhoneValue(value: unknown): string {
    const normalized = this.normalizePhoneValue(value);
    if (!/^\+\d+$/.test(normalized)) return this.formatDisplayValue(value);
    const digits = normalized.slice(1);
    return `+${(digits.match(/\d{1,3}/g) ?? []).join(' ')}`;
  }

  private normalizeFieldKey(value: unknown): string {
    return this.stringifyValue(value)
      .trim()
      .toLowerCase()
      .replace(/^x_+/, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  private stringifyValue(value: unknown): string {
    if (value === undefined || value === null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return String(value);
    if (value instanceof Date) return value.toLocaleDateString();

    return '';
  }

  private formatPhones(phones: ICardPhone[] | null | undefined, fallback: string = '-'): string {
    return (
      phones
        ?.map(getCardPhoneValue)
        .filter(Boolean)
        .join(', ') || fallback
    );
  }

  private formatEmails(emails: ICardEmail[] | null | undefined, fallback: string = '-'): string {
    return (
      emails
        ?.map((item) => item.email)
        .filter(Boolean)
        .join(', ') || fallback
    );
  }
}
